import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import StoreIcon from "@mui/icons-material/StoreTwoTone";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  updateEntity,
  saveValue,
  getASICData,
  userDetailsSelector,
  removeEntity,
  addAddress,
} from "../store/slices/applicationFormSlice";
import readableABNString from "../utils/readableABNString";
import readableACNString from "../utils/readableACNString";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Email from "./Email";
import PhoneNumber from "./PhoneNumber";
import GoogleAddressSearch from "./GoogleAddressSearch";

const Entity = ({
  applicationId,
  entity,
  confirmRemoveEntity,
  setConfirmRemoveEntity,
}) => {
  const dispatch = useDispatch();
  const { gettingASICData } = useSelector(userDetailsSelector);
  const [turnoverValue, setTurnoverValue] = useState(entity?.turnover || 0);
  const [industryTypeValue, setIndustryTypeValue] = useState(
    entity?.industryType
  );
  const [getASICDataButtonBackground, setGetASICDataButtonBackground] =
    useState("secondary");
  const [getASICDataButtonText, setGetASICDataButtonText] =
    useState("Get ASIC data");

  const handleEntity = (update) => {
    dispatch(updateEntity(update));
  };

  function handleGetASICData() {
    setGetASICDataButtonBackground("success");
    setGetASICDataButtonText("ASIC Data retrieved");

    setTimeout(() => {
      setGetASICDataButtonBackground("secondary");
      setGetASICDataButtonText("Get ASIC data");
      dispatch(saveValue({ gotASICData: false }));
    }, 4000);
  }

  useEffect(() => {
    if (gettingASICData === false) {
      handleGetASICData();
      dispatch(saveValue({ gettingASICData: null }));
    }
  }, [gettingASICData]);

  const handleAddAddress = (newAddressValue, entityId) => {
    if (newAddressValue) {
      dispatch(
        addAddress({
          entityId,
          address: newAddressValue,
        })
      );
    }
  };

  return (
    <Accordion
      defaultExpanded
      id={`entity-${entity._id}`}
      elevation={1}
      style={{ padding: "0 15px" }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel2a-content"
        id="panel2a-header"
      >
        <StoreIcon style={{ marginRight: "15px" }} />
        <Typography>{entity.entityName}</Typography>
        {!confirmRemoveEntity[entity._id] && (
          <Button
            size="small"
            style={{
              padding: 0,
              marginLeft: "20px",
            }}
            onClick={() => setConfirmRemoveEntity({ [entity._id]: true })}
          >
            Remove
          </Button>
        )}
        {confirmRemoveEntity[entity._id] && (
          <Stack direction="row" style={{ marginLeft: "20px" }}>
            <Typography>Are you sure?</Typography>
            <Button
              size="small"
              style={{
                padding: 0,
                marginLeft: "20px",
              }}
              onClick={() => setConfirmRemoveEntity({ [entity._id]: false })}
            >
              Cancel
            </Button>
            <Button
              size="small"
              style={{
                padding: 0,
                marginLeft: "20px",
                color: "#d32f2f",
              }}
              onClick={() => {
                dispatch(
                  removeEntity({
                    applicationId,
                    entityId: entity._id,
                  })
                );
                setConfirmRemoveEntity({ [entity._id]: false });
              }}
            >
              Remove
            </Button>
          </Stack>
        )}
      </AccordionSummary>

      <AccordionDetails style={{ paddingBottom: "40px" }}>
        <Grid item container style={{ margin: "0 0 20px 0" }}>
          {entity?.abn && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                ABN
              </Typography>
              <Typography variant="body2">
                {readableABNString(entity?.abn)}
              </Typography>
            </Grid>
          )}

          {entity?.acn && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                ACN
              </Typography>
              <Typography variant="body2">
                {readableACNString(entity?.acn)}
              </Typography>
            </Grid>
          )}

          {entity?.gst && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                GST
              </Typography>
              <Typography variant="body2">
                {entity?.gst
                  ? `Reg. ${moment(entity?.gst).format("DD MMM YY")}`
                  : "No"}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid container style={{ margin: "0 0 20px 0" }}>
          {entity?.entityName && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Entity name
              </Typography>
              <Typography variant="body2">{entity?.entityName}</Typography>
              {/* {entityNameErr ? (
                <span style={{ color: "red" }}>{entityNameErr}</span>
              ) : null} */}
            </Grid>
          )}

          {entity?.entityType && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Entity type
              </Typography>
              <Typography variant="body2">{entity?.entityType}</Typography>
            </Grid>
          )}

          {entity?.timeInBusiness && (
            <Grid item lg={4} md={4} sm={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Time in business
              </Typography>
              <Typography variant="body2">
                {`${moment().diff(
                  moment(entity?.timeInBusiness),
                  "months"
                )} months`}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container style={{ margin: "0 0 20px 0" }}>
          <Grid item xs={12}>
            {entity?.businessNames?.length > 0 && (
              <>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Business names
                </Typography>
                <Typography variant="body2">
                  {entity?.businessNames.map((name, i) => (
                    <span>
                      {entity?.businessNames.length - 1 === i
                        ? `${name}`
                        : `${name}, `}
                    </span>
                  ))}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        <Grid container style={{ margin: "0 0 20px 0" }}>
          {entity?.addresses?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Business addresses
              </Typography>
              {entity?.addresses
                ?.filter((address) => address.status !== "previous")
                .map((address) => (
                  <Typography variant="body2" style={{ margin: "0 0 10px" }}>
                    {address.fullAddress}
                  </Typography>
                ))}
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item sm={4}>
            <Email
              id={`email_${entity._id}`}
              entityId={entity?._id}
              email={
                entity
                  ? entity?.emails.find(
                      (a) => a.status === "current" && a.address
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item sm={4}>
            <PhoneNumber
              id={`phoneNumber_${entity._id}`}
              entityId={entity?._id}
              phoneNumber={
                entity
                  ? entity?.phoneNumbers.find(
                      (a) => a.status === "current" && a.number
                    )
                  : ""
              }
            />
          </Grid>
          {entity?.addresses?.filter((address) => address.status !== "previous")
            ?.length <= 1 && (
            <Grid item sm={12}>
              <GoogleAddressSearch
                id={`address_${entity._id}`}
                handleCreateAddress={(address) =>
                  handleAddAddress(address, entity._id)
                }
                address={
                  entity ? entity?.addresses[entity.addresses.length - 1] : ""
                }
              />
            </Grid>
          )}
          <Grid item md={4} sm={5}>
            <TextField
              id={`turnover_${entity._id}`}
              type="text"
              name="turnover"
              label="Turnover (yearly)"
              variant="outlined"
              size="small"
              value={turnoverValue}
              onChange={(event) => setTurnoverValue(event.target.value)}
              onBlur={(event) => {
                handleEntity({
                  entityId: entity?._id,
                  turnover: Number(event.target.value) || 0,
                });
              }}
              // error={!turnoverValue}
              // helperText={!turnoverValue ? "Required" : ""}
            />
          </Grid>
          <Grid item md={8} sm={7}>
            <TextField
              fullWidth
              id={`industryType_${entity._id}`}
              type="text"
              name="industryType"
              label="Industry or business type"
              variant="outlined"
              size="small"
              value={industryTypeValue || ""}
              onChange={(event) => setIndustryTypeValue(event.target.value)}
              onBlur={(event) => {
                handleEntity({
                  entityId: entity?._id,
                  industryType: event.target.value,
                });
              }}
              error={!industryTypeValue}
              helperText={!industryTypeValue ? "Required" : ""}
            />
          </Grid>
          {entity?.acn && entity?.addresses?.length < 1 && (
            <Grid item md={12} sm={12}>
              <LoadingButton
                loading={gettingASICData}
                color={getASICDataButtonBackground}
                variant="outlined"
                onClick={() => {
                  dispatch(saveValue({ gettingASICData: true }));
                  dispatch(
                    getASICData({
                      applicationId,
                      acn: entity.acn,
                      entityId: entity._id,
                    })
                  );
                }}
              >
                {getASICDataButtonText}
              </LoadingButton>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default Entity;
